import { MapPin, Scale, Award, MessageSquare } from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import DynamicGradient from "@/components/ui/DynamicGradient";

const ProblemSolutionSection = () => {
  const { t } = useTranslation('home');
  // Altum's 4 Pillars
  const pillars = [
    {
      icon: MapPin,
      title: t('whyChoose.pillar1.title'),
      description: t('whyChoose.pillar1.description'),
    },
    {
      icon: Scale,
      title: t('whyChoose.pillar2.title'),
      description: t('whyChoose.pillar2.description'),
    },
    {
      icon: Award,
      title: t('whyChoose.pillar3.title'),
      description: t('whyChoose.pillar3.description'),
    },
    {
      icon: MessageSquare,
      title: t('whyChoose.pillar4.title'),
      description: t('whyChoose.pillar4.description'),
    },
  ];

  return (
    <DynamicGradient className="py-20 md:py-28 bg-accent">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <span className="section-eyebrow">{t('whyChoose.eyebrow')}</span>
          <h2 className="text-[32px] md:text-[48px] font-heading font-bold text-secondary mb-4">
            {t('whyChoose.headline')}
          </h2>
          <p className="text-lg text-charcoal/80 max-w-2xl mx-auto">
            {t('whyChoose.subheadline')}
          </p>
        </motion.div>

        {/* 4-Card Grid - Always 4 in a row on desktop */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {pillars.map((pillar, index) => (
            <motion.div
              key={index}
              className="bg-white p-8 rounded-lg border border-border shadow-sm min-h-[280px] flex flex-col items-center text-center group card-apple"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{
                duration: 0.6,
                delay: index * 0.1,
                ease: [0.25, 0.46, 0.45, 0.94],
              }}
              whileHover={{
                y: -8,
                boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
              }}
            >
              {/* Icon */}
              <motion.div
                className="mb-6"
                whileHover={{ scale: 1.1 }}
                transition={{ duration: 0.3 }}
              >
                <pillar.icon className="w-10 h-10 text-primary" strokeWidth={1.5} />
              </motion.div>

              {/* Headline */}
              <h3 className="text-xl font-heading font-bold mb-4 text-secondary group-hover:text-primary transition-colors duration-300">
                {pillar.title}
              </h3>

              {/* Copy */}
              <p className="text-sm text-charcoal leading-[1.6]">
                {pillar.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </DynamicGradient>
  );
};

export default ProblemSolutionSection;
